import { Link } from '@tanstack/react-router';
import { ImageIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ScheduleMember, ScheduleStore } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { lastLiveAt } from '../lib/next-up';

import { PositionInput } from './position-input';
import { PlacementBadge } from './schedule-status-badge';

export interface MemberColumnsOptions {
  stores: readonly ScheduleStore[];
  canWrite: boolean;
  /** Member ids with a change in flight: their controls wait. */
  busy: ReadonlySet<string>;
  /** Members most likely in the next batch (empty when the whole pool is not on the page). */
  nextUp: ReadonlySet<string>;
  onChange: (
    member: ScheduleMember,
    body: { pinned?: boolean; excluded?: boolean; position?: number },
  ) => void;
}

/** Column model of the pool table. */
export function useMemberColumns({
  stores,
  canWrite,
  busy,
  nextUp,
  onChange,
}: MemberColumnsOptions) {
  const { t } = useTranslation(['schedules', 'common']);
  const format = useFormat();
  const storeNames = useMemo(() => new Map(stores.map((s) => [s.storeId, s.storeName])), [stores]);

  return useMemo<Array<DataTableColumnDef<ScheduleMember>>>(
    () => [
      {
        id: 'product',
        header: t('schedules:pool.columns.product'),
        cell: ({ row }) => {
          const { product } = row.original;
          return (
            <div className="flex min-w-0 items-center gap-3">
              {product.featuredImage ? (
                <img
                  src={product.featuredImage.url}
                  alt={product.featuredImage.alt ?? ''}
                  className="size-9 shrink-0 rounded object-cover"
                />
              ) : (
                <span className="bg-muted flex size-9 shrink-0 items-center justify-center rounded">
                  <ImageIcon aria-hidden className="text-muted-foreground size-4" />
                </span>
              )}
              <div className="min-w-0">
                <Link
                  to="/org/products/$productId"
                  params={{ productId: row.original.productId }}
                  className="block truncate font-medium hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {product.title}
                </Link>
                <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  <code className="truncate">{product.handle}</code>
                  {product.deleted ? (
                    <Badge variant="outline" className="text-destructive h-4 px-1 text-[10px]">
                      {t('schedules:pool.deleted')}
                    </Badge>
                  ) : product.status !== 'active' ? (
                    <Badge variant="outline" className="h-4 px-1 text-[10px]">
                      {t(`common:status.${product.status}`)}
                    </Badge>
                  ) : null}
                  {nextUp.has(row.original.productId) ? (
                    <Badge
                      variant="outline"
                      className="bg-primary/10 h-4 border-transparent px-1 text-[10px]"
                      title={t('schedules:pool.nextUpHint')}
                    >
                      {t('schedules:pool.nextUp')}
                    </Badge>
                  ) : null}
                </span>
              </div>
            </div>
          );
        },
        meta: { lockVisibility: true },
      },
      {
        id: 'source',
        accessorKey: 'source',
        header: t('schedules:pool.columns.source'),
        cell: ({ row }) => (
          <Badge variant="outline">{t(`schedules:source.${row.original.source}`)}</Badge>
        ),
        meta: { filterKey: 'source' },
        size: 100,
      },
      {
        id: 'stores',
        header: t('schedules:pool.columns.stores'),
        cell: ({ row }) =>
          row.original.placements.length === 0 ? (
            <span className="text-muted-foreground text-xs">
              {t('schedules:pool.noPlacements')}
            </span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {row.original.placements.map((placement) => (
                <PlacementBadge
                  key={placement.storeId}
                  state={placement.state}
                  label={storeNames.get(placement.storeId) ?? undefined}
                />
              ))}
            </span>
          ),
      },
      {
        id: 'lastLive',
        header: t('schedules:pool.columns.lastLive'),
        cell: ({ row }) => {
          const last = lastLiveAt(row.original);
          if (!last) {
            return <span className="text-muted-foreground">{t('schedules:pool.neverLive')}</span>;
          }
          const perStore = row.original.placements
            .flatMap((p) =>
              p.lastLiveAt
                ? [
                    t('schedules:pool.lastLiveIn', {
                      store: storeNames.get(p.storeId) ?? t('schedules:errors.aStore'),
                      when: format.dateTime(p.lastLiveAt),
                    }),
                  ]
                : [],
            )
            .join('\n');
          return (
            <span className="text-muted-foreground whitespace-nowrap" title={perStore}>
              {format.relative(last)}
            </span>
          );
        },
        size: 120,
      },
      {
        id: 'pinned',
        accessorKey: 'pinned',
        header: t('schedules:pool.columns.pinned'),
        cell: ({ row }) => (
          <Switch
            checked={row.original.pinned}
            disabled={!canWrite || busy.has(row.original.productId)}
            aria-label={t('schedules:pool.pinFor', { title: row.original.product.title })}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) => onChange(row.original, { pinned: checked })}
          />
        ),
        meta: { filterKey: 'pinned', tour: 'schedules.detail.pool-pin' },
        size: 90,
      },
      {
        id: 'excluded',
        accessorKey: 'excluded',
        header: t('schedules:pool.columns.excluded'),
        cell: ({ row }) => (
          <Switch
            checked={row.original.excluded}
            disabled={!canWrite || busy.has(row.original.productId)}
            aria-label={t('schedules:pool.excludeFor', { title: row.original.product.title })}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) => onChange(row.original, { excluded: checked })}
          />
        ),
        meta: { filterKey: 'excluded' },
        size: 90,
      },
      {
        id: 'position',
        accessorKey: 'position',
        header: t('schedules:pool.columns.position'),
        cell: ({ row }) => (
          <PositionInput
            key={row.original.position}
            member={row.original}
            disabled={!canWrite || busy.has(row.original.productId)}
            onCommit={(position) => onChange(row.original, { position })}
          />
        ),
        size: 110,
      },
    ],
    [busy, canWrite, format, nextUp, onChange, storeNames, t],
  );
}
